import type {
  DynamicBillSummaryNode,
  DynamicBillSummaryTab,
  DynamicBillSummaryPlotMapping,
  DynamicBillSummaryRunMatrixMapping,
  DynamicBillSummaryResourceCodeEntry,
} from "../types/tender.types";
import type { BoqSummaryLineItem, DrainageSummaryLineItem } from "./groundworksBoqSummaryComputer";

/**
 * A Dynamic Bill Summary node serialised into the exact field shape every
 * fixed `groundworks.summary` domain array already uses (`BoqSummaryLineItem`
 * in groundworksBoqSummaryComputer.ts: key/description/quantity/unit/rate/
 * amount/children) — a plain line-item array, not a `{ id, label, nodes }`
 * wrapper. Section rows carry `children` (always present, even `[]`, so a
 * reader can tell a section from a leaf); leaf rows never have `children`.
 */
export interface DynamicBillSummaryLineItem {
  key: string;
  description: string;
  quantity?: number;
  unit?: string;
  rate?: number;
  amount: number;
  amountType?: 'number' | 'text';
  amountText?: string;
  resourceCode?: string;
  resourceCodes?: DynamicBillSummaryResourceCodeEntry[];
  // Plot Matrix integration (dev-mode-only) — passed through as-is, same
  // shape as DynamicBillSummaryPlotMapping.
  plotMapping?: DynamicBillSummaryPlotMapping;
  // Run Matrix integration (Roads & Sewers only) — same passed-through
  // convention as plotMapping above.
  runMatrixMapping?: DynamicBillSummaryRunMatrixMapping;
  children?: DynamicBillSummaryLineItem[];
}

const isSectionNode = (
  node: DynamicBillSummaryNode,
): node is Extract<DynamicBillSummaryNode, { type: "section" }> => node.type === "section";

export const isDynamicBillSummarySectionNode = isSectionNode;

/** Generic node-tree map/remove helpers, shared by anywhere a DynamicBillSummaryNode
 *  tree is edited — DynamicBillSummaryPanel.tsx's own tabs, and ad-hoc extra-item
 *  buckets like GroundworksCutFillBillSummary.tsx's `cutFillsExtraItems`. */
export const mapDynamicBillSummaryNodes = (
  nodes: DynamicBillSummaryNode[],
  id: string,
  fn: (node: DynamicBillSummaryNode) => DynamicBillSummaryNode,
): DynamicBillSummaryNode[] =>
  nodes.map((node) => {
    if (node.id === id) return fn(node);
    if (isSectionNode(node)) return { ...node, children: mapDynamicBillSummaryNodes(node.children, id, fn) };
    return node;
  });

export const removeDynamicBillSummaryNodeById = (
  nodes: DynamicBillSummaryNode[],
  id: string,
): DynamicBillSummaryNode[] =>
  nodes
    .filter((node) => node.id !== id)
    .map((node) =>
      isSectionNode(node) ? { ...node, children: removeDynamicBillSummaryNodeById(node.children, id) } : node,
    );

/** The marked-up Rate a dynamic item's Rate column should display —
 *  `markupFactor` (default 1, i.e. no-op) applies OH&P/Preliminaries the same
 *  way every fixed Bill Summary already applies it to its own rows —
 *  `(1 + overheadAndProfit/100) * (1 + preliminaries/100)` — multiplied onto
 *  the node's raw stored (item-library) rate, then rounded to 2dp. Rounding
 *  happens AFTER markup, not before — Amount must equal `displayed Rate ×
 *  Qty` exactly, matching the R&S Bill Summary rate-rounding convention
 *  (roadsAndSewersBoqSummaryComputer.ts and friends). Callers with access to
 *  `settings.groundworks` (components/hooks via `useGlobalSettings()`) pass
 *  their own live `markupFactor`; callers that don't (this being a plain
 *  utility) get the raw, unmarked-up (but still rounded) rate by omitting it. */
export const computeDynamicBillSummaryNodeRate = (
  rate: number | undefined,
  markupFactor: number = 1,
): number => parseFloat(((rate ?? 0) * markupFactor).toFixed(2));

/** Section amount = sum of children; leaf amount = qty × the same rounded,
 *  marked-up rate `computeDynamicBillSummaryNodeRate` returns (see there),
 *  falling back to a manually-set amount when qty/rate aren't both present. */
export const computeDynamicBillSummaryNodeAmount = (
  node: DynamicBillSummaryNode,
  markupFactor: number = 1,
): number => {
  if (isSectionNode(node)) {
    return node.children.reduce((sum, child) => sum + computeDynamicBillSummaryNodeAmount(child, markupFactor), 0);
  }
  if (node.amountType === "text") return 0;
  const qty = Math.abs(node.qty ?? 0);
  const rate = computeDynamicBillSummaryNodeRate(node.rate, markupFactor);
  return qty && rate ? qty * rate : node.amount ?? 0;
};

/**
 * camelCase key derived from a tab's label — same naming convention every
 * other `groundworks.summary` domain key already uses (`cutFills`,
 * `plotDrives`, `geoWalls`, etc), so a tab named "Others" persists as
 * `summary.others`, not buried inside one combined array.
 */
export const dynamicBillSummaryTabKey = (label: string): string => {
  const words = label.trim().split(/[^a-zA-Z0-9]+/).filter(Boolean);
  if (words.length === 0) return "tab";
  return words
    .map((word, i) => (i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()))
    .join("");
};

/** Reverse of `dynamicBillSummaryTabKey` — since the persisted format has no
 *  separate label field, the tab's display label is rebuilt from its own
 *  summary key ("extraPreliminaries" -> "Extra Preliminaries"). */
const humanizeDynamicBillSummaryKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * Combines a multi-code ad-hoc extra item's `resourceCodes` (+/- operator
 * entries) into the same single joined-string shape every fixed BoQ row's own
 * `resourceCode` already uses for a code combo (e.g. `"ALP031A+GWALP004"` —
 * see `computeBcCalculation`/`BcCalculationDetails.tsx`, which parses it back
 * apart by splitting on `+`). Both extra items AND Dynamic Bill Summary tabs
 * persist into `groundworks.summary`, the exact same field shape every fixed
 * domain array already uses, and downstream consumers of that data
 * (central-repo `tasks[].resource[]`, the BC integration) only ever read
 * `resourceCode` — they have no notion of a separate `resourceCodes` field —
 * so a multi-code item must fold into that same singular field to be seen at
 * all. Single-code items pass `node.resourceCode` straight through.
 */
export const combineResourceCodes = (node: DynamicBillSummaryNode): string | undefined => {
  if (isSectionNode(node)) return undefined;
  if (node.resourceCodes && node.resourceCodes.length > 0) {
    return node.resourceCodes
      .map((entry, i) => (i === 0 ? entry.resourceCode : `${entry.operator}${entry.resourceCode}`))
      .join("");
  }
  return node.resourceCode;
};

/**
 * Reverse of `combineResourceCodes` — splits a persisted `resourceCode` back
 * into `resourceCodes` entries whenever it's actually a joined multi-code
 * combo (contains a `+`/`-` between codes), so a multi-code item still
 * shows/edits as multiple codes after a reload instead of one long literal
 * code. A plain single code (no operator) returns `undefined` — the caller
 * keeps it in `resourceCode` as before. First entry's operator is always
 * `"+"` (ignored on calculation, same convention `resourceCodes` already
 * follows) since a leading `+`/`-` cannot survive `combineResourceCodes`
 * (its first entry is never prefixed).
 */
export const splitResourceCode = (resourceCode: string | undefined): DynamicBillSummaryResourceCodeEntry[] | undefined => {
  if (!resourceCode) return undefined;
  const matches = resourceCode.match(/[+-]?[^+-]+/g);
  if (!matches || matches.length < 2) return undefined;
  return matches.map((m, i) => ({
    resourceCode: m.replace(/^[+-]/, "").trim(),
    operator: i === 0 ? "+" : (m.startsWith("-") ? "-" : "+"),
  }));
};

export const nodeToLineItem = (node: DynamicBillSummaryNode): DynamicBillSummaryLineItem => {
  const amount = computeDynamicBillSummaryNodeAmount(node);
  if (isSectionNode(node)) {
    return {
      key: node.id,
      description: node.label,
      amount,
      children: node.children.map(nodeToLineItem),
    };
  }
  return {
    key: node.id,
    description: node.description,
    quantity: node.qty,
    unit: node.unit,
    rate: node.rate,
    amount,
    amountType: node.amountType,
    amountText: node.amountText,
    resourceCode: node.resourceCode,
    resourceCodes: node.resourceCodes,
    plotMapping: node.plotMapping,
    runMatrixMapping: node.runMatrixMapping,
  };
};

/**
 * Persistence-only counterpart to `nodeToLineItem` — same shape, but folds a
 * multi-code item's `resourceCodes` into the same joined-string `resourceCode`
 * `combineResourceCodes` produces for ad-hoc extra items, instead of carrying
 * `resourceCodes` through untouched. Dynamic Bill Summary tabs persist into
 * `groundworks.summary`, the exact same field shape every fixed domain array
 * uses, and downstream consumers of that data (central-repo
 * `tasks[].resource[]`, the BC integration) only ever read `resourceCode` —
 * so this is what `serializeDynamicBillSummaryTabs` sends, while
 * `nodeToLineItem` itself stays untouched for its other (display-fallback)
 * caller in DynamicBillSummaryPanel.tsx, which still wants the raw
 * `resourceCodes` array to render the multi-code breakdown.
 */
const nodeToPersistedLineItem = (node: DynamicBillSummaryNode): DynamicBillSummaryLineItem => {
  if (isSectionNode(node)) {
    return {
      key: node.id,
      description: node.label,
      amount: computeDynamicBillSummaryNodeAmount(node),
      children: node.children.map(nodeToPersistedLineItem),
    };
  }
  const item = nodeToLineItem(node);
  return { ...item, resourceCode: combineResourceCodes(node), resourceCodes: undefined };
};

const lineItemToNode = (item: DynamicBillSummaryLineItem): DynamicBillSummaryNode => {
  if (Array.isArray(item.children)) {
    return {
      id: item.key,
      type: "section",
      label: item.description,
      children: item.children.map(lineItemToNode),
    };
  }
  const resourceCodes = splitResourceCode(item.resourceCode) ?? item.resourceCodes;
  return {
    id: item.key,
    type: "item",
    description: item.description,
    qty: item.quantity,
    unit: item.unit,
    rate: item.rate,
    amountType: item.amountType,
    amountText: item.amountText,
    ...(resourceCodes ? { resourceCodes } : { resourceCode: item.resourceCode }),
    plotMapping: item.plotMapping,
    runMatrixMapping: item.runMatrixMapping,
  };
};

// The fixed domain keys `computeGroundworksBoqSummary` always writes to
// `groundworks.summary` (GroundworksBoqSummary) — never treated as a Dynamic
// Bill Summary tab even if their shape happened to match one.
const FIXED_SUMMARY_KEYS = new Set([
  "foundations",
  "cutFills",
  "garden",
  "plotDrives",
  "plotPavings",
  "scaffold",
  "plotService",
  "drainage",
  "retainingWalls",
  "geoWalls",
]);

const isDynamicBillSummaryLineItemArray = (value: unknown): value is DynamicBillSummaryLineItem[] =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      !!item &&
      typeof item === "object" &&
      typeof (item as Record<string, unknown>).key === "string" &&
      typeof (item as Record<string, unknown>).description === "string" &&
      typeof (item as Record<string, unknown>).amount === "number",
  );

/**
 * Serialise Dynamic Bill Summary tabs into individual `groundworks.summary`
 * keys, one per tab (same flat "key holds this domain's line-item array"
 * shape every fixed summary key already uses) — a tab named "Others" becomes
 * `summary.others = DynamicBillSummaryLineItem[]`, not
 * `summary.dynamicBillSummaryTabs[].nodes`.
 */
export const serializeDynamicBillSummaryTabs = (
  tabs: DynamicBillSummaryTab[],
): Record<string, DynamicBillSummaryLineItem[]> => {
  const out: Record<string, DynamicBillSummaryLineItem[]> = {};
  tabs.forEach((tab) => {
    out[dynamicBillSummaryTabKey(tab.label)] = tab.nodes.map(nodeToPersistedLineItem);
  });
  return out;
};

/**
 * Reverse of `serializeDynamicBillSummaryTabs` — reconstructs the tab array
 * from a fetched `groundworks.summary` object by picking out every entry
 * that looks like a Dynamic Bill Summary line-item array, skipping the fixed
 * domain keys. The tab's `id`/label are rebuilt from its summary key, since
 * the persisted format carries no separate metadata for them.
 */
/**
 * Same tab-key filtering as `deserializeDynamicBillSummaryTabs`, but returns
 * the raw `DynamicBillSummaryLineItem[]` per tab key instead of converting to
 * `DynamicBillSummaryNode` — the raw shape still carries the `amount` that
 * was actually persisted, which the SummaryKey column needs to compare
 * against the live-recomputed amount (a `DynamicBillSummaryNode` never
 * stores `amount`, it's always recomputed from qty*rate).
 */
export const getRawDynamicBillSummaryTabItems = (
  summary: Record<string, unknown> | undefined | null,
): Record<string, DynamicBillSummaryLineItem[]> => {
  if (!summary) return {};
  const out: Record<string, DynamicBillSummaryLineItem[]> = {};
  Object.entries(summary).forEach(([key, value]) => {
    if (!FIXED_SUMMARY_KEYS.has(key) && isDynamicBillSummaryLineItemArray(value)) {
      out[key] = value as DynamicBillSummaryLineItem[];
    }
  });
  return out;
};

/** Flattens a persisted line-item tree (sections + leaves) into one
 *  `key -> item` map, for O(1) SummaryKey lookups by node id. */
export const flattenDynamicBillSummaryLineItems = (
  items: DynamicBillSummaryLineItem[],
  out: Map<string, DynamicBillSummaryLineItem> = new Map(),
): Map<string, DynamicBillSummaryLineItem> => {
  items.forEach((item) => {
    out.set(item.key, item);
    if (item.children) flattenDynamicBillSummaryLineItems(item.children, out);
  });
  return out;
};

export const deserializeDynamicBillSummaryTabs = (
  summary: Record<string, unknown> | undefined | null,
): DynamicBillSummaryTab[] => {
  if (!summary) return [];
  return Object.entries(summary)
    .filter(([key, value]) => !FIXED_SUMMARY_KEYS.has(key) && isDynamicBillSummaryLineItemArray(value))
    .map(([key, value]) => ({
      id: key,
      label: humanizeDynamicBillSummaryKey(key),
      nodes: (value as DynamicBillSummaryLineItem[]).map(lineItemToNode),
    }));
};

export interface DynamicBcCalculationResult {
  codes: Array<{ resourceCode: string; operator: "+" | "-"; dbRate: number }>;
  rate: number;
  quantity: number;
  amount: number;
  steps: string[];
}

/**
 * SummaryKey modal's "BCCalculation" block for any Dynamic-Bill-Summary-shaped
 * item — used by DynamicBillSummaryPanel.tsx's own tabs, and by
 * GroundworksCutFillBillSummary.tsx's ad-hoc extra items (which reuse the same
 * `resourceCode`/`resourceCodes` fields). Same "backend dbRate-only derivation"
 * convention every fixed Bill Summary's SummaryKey modal already shows
 * (`computeBcCalculation`, `BcCalculationDetails.tsx`), but built off
 * `resourceCodes`' own +/- operators directly instead of `computeBcCalculation`'s
 * resourceCode-string parsing (which only sums/averages, no subtraction) — these
 * are the only Bill Summary rows that can carry a `-` operator between codes. A
 * single `resourceCode` is the one-entry case.
 */
export const computeDynamicBcCalculation = (
  item: DynamicBillSummaryLineItem,
  getDbRate: (code: string) => number,
): DynamicBcCalculationResult | null => {
  if (item.amountType === "text") return null;
  // A record read straight off the persisted payload (e.g. `apiLineItemsById`/
  // `apiLineItemsByTabId` — the raw `groundworks.summary.<tabName>` line items,
  // never reconstructed into nodes via `lineItemToNode`) carries a multi-code
  // item's codes combined into the single `resourceCode` string
  // (`combineResourceCodes`/`nodeToPersistedLineItem`), not the `resourceCodes`
  // array — only the live, in-memory node tree has that. Falling back to
  // `splitResourceCode` here means this still resolves correctly either way,
  // instead of treating the whole combined string as one (unmatched) code and
  // silently computing a £0.00 rate.
  const codeEntries: DynamicBillSummaryResourceCodeEntry[] =
    item.resourceCodes && item.resourceCodes.length > 0
      ? item.resourceCodes
      : item.resourceCode
        ? splitResourceCode(item.resourceCode) ?? [{ resourceCode: item.resourceCode, operator: "+" }]
        : [];
  if (codeEntries.length === 0) return null;
  const roundedQuantity = Math.round(Math.abs(item.quantity || 0));
  const codes = codeEntries.map((entry) => ({ ...entry, dbRate: getDbRate(entry.resourceCode) }));
  const rate = parseFloat(
    codes
      .reduce((acc, entry, i) => {
        if (i === 0) return entry.dbRate;
        return entry.operator === "-" ? acc - entry.dbRate : acc + entry.dbRate;
      }, 0)
      .toFixed(2),
  );
  const amount = parseFloat((rate * roundedQuantity).toFixed(2));
  const rateLine =
    codes
      .map((entry, i) =>
        i === 0
          ? `£${entry.dbRate.toFixed(4)} (${entry.resourceCode})`
          : `${entry.operator} £${entry.dbRate.toFixed(4)} (${entry.resourceCode})`,
      )
      .join(" ") + ` = £${rate.toFixed(2)} rate`;
  return {
    codes,
    rate,
    quantity: roundedQuantity,
    amount,
    steps: [
      rateLine,
      `£${rate.toFixed(2)} × ${roundedQuantity.toLocaleString("en-GB")} qty = £${amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    ],
  };
};

const extraNodeToBoqLine = (node: DynamicBillSummaryNode, attachKey: string): BoqSummaryLineItem => {
  if (isDynamicBillSummarySectionNode(node)) {
    // Persist the section itself (zero qty/rate, amount = its children's own
    // total via the same computeDynamicBillSummaryNodeAmount every other
    // section-amount rollup in this file uses) — previously only leaf
    // descendants were flattened here, discarding the section wrapper (and
    // its name) entirely, and re-attaching its children straight to the
    // OUTER attachment point (see the `walk(node.children, attachKey)` this
    // replaced) instead of the section they actually belonged to. `children`
    // nests the section's own descendants (`BoqSummaryLineItem.children` is
    // already used the same way by e.g. Scaffold's own summary rows), each
    // walked with the SECTION'S OWN id as ITS attachKey so they round-trip
    // back under this section on reload instead of `attachKey`.
    return {
      key: node.id,
      fieldName: node.id,
      description: node.label,
      quantity: 0,
      unit: "",
      rate: 0,
      amount: computeDynamicBillSummaryNodeAmount(node),
      category: attachKey,
      children: node.children.map((child) => extraNodeToBoqLine(child, node.id)),
    };
  }
  // Text-type items (e.g. "Included", "Rate Only") have no qty/rate of their
  // own but still need to persist — same convention Dynamic Bill Summary
  // tabs' own serializeDynamicBillSummaryTabs follows (every node persists,
  // never just the ones with a computed amount) — so a reload doesn't
  // silently drop the row and renumber everything after it.
  return {
    key: node.id,
    fieldName: node.id,
    description: node.description,
    quantity: node.qty ?? 0,
    unit: node.unit ?? "",
    rate: node.rate ?? 0,
    amount: computeDynamicBillSummaryNodeAmount(node),
    category: attachKey,
    resourceCode: combineResourceCodes(node),
    plotMapping: node.plotMapping,
    runMatrixMapping: node.runMatrixMapping,
    amountType: node.amountType,
    amountText: node.amountText,
  };
};

/**
 * Flattens Cut & Fill's / Foundations' ad-hoc extra items
 * (`state.groundworks.cutFillsExtraItems`/`.foundationsExtraItems`, see
 * useBillSummaryExtraItems.tsx) into the same `BoqSummaryLineItem[]` shape the
 * computed `summary.cutFills`/`summary.foundations` array already uses, so
 * they persist into the real BOQ document and get picked back up as a genuine
 * SummaryKey comparison on the next load. Used by both BOQ save paths —
 * `boqPayloadBuilder.ts`'s `createBoqPayload` and `boqVersionUtils.ts`'s
 * `forceSaveBothBoqEndpoints` — since each builds its own `summary` object
 * independently. `fieldName` is set to the node's own `id` since these have no
 * natural field name — the review page's SummaryKey lookup matches extra rows
 * by that same `key`/`id`. `category` carries the item's own attachment point
 * (`"__root__"`, or the existing row's `key` it's grafted onto) rather than a
 * generic "Extra" label — every other persisted line's `category` identifies
 * where in the tree it belongs, and extra items need the same so a reader (or
 * `deserializeCutFillsExtraItems` below) can place each one back under the
 * right row instead of losing that placement once flattened into one array.
 * A section node's own children are nested under its own persisted line
 * instead of being flattened alongside it (see `extraNodeToBoqLine` above) —
 * still only one array walk, only ever descending into `node.children` for a
 * section, same summing behaviour any flat consumer already expects since a
 * section's own `amount` already equals its children's total.
 */
export function flattenCutFillsExtraItemsToBoqLines(
  buckets: Record<string, DynamicBillSummaryNode[]> | undefined,
): BoqSummaryLineItem[] {
  if (!buckets) return [];
  const out: BoqSummaryLineItem[] = [];
  Object.entries(buckets).forEach(([attachKey, bucket]) => {
    bucket.forEach((node) => out.push(extraNodeToBoqLine(node, attachKey)));
  });
  return out;
}

const boqLineToExtraNode = (line: Partial<BoqSummaryLineItem>): DynamicBillSummaryNode => {
  if (Array.isArray(line.children)) {
    return {
      id: line.key ?? line.fieldName ?? "",
      type: "section",
      label: line.description ?? "",
      children: line.children.map(boqLineToExtraNode),
    };
  }
  const isText = line.amountType === "text";
  return {
    id: line.key ?? line.fieldName ?? "",
    type: "item",
    description: line.description ?? "",
    qty: isText ? undefined : line.quantity,
    unit: isText ? undefined : line.unit,
    rate: isText ? undefined : line.rate,
    amountType: isText ? "text" : "number",
    ...(isText && { amountText: line.amountText ?? "" }),
    ...((): { resourceCode?: string; resourceCodes?: DynamicBillSummaryResourceCodeEntry[] } => {
      const resourceCodes = splitResourceCode(line.resourceCode);
      return resourceCodes ? { resourceCodes } : line.resourceCode ? { resourceCode: line.resourceCode } : {};
    })(),
    ...(line.plotMapping && { plotMapping: line.plotMapping }),
    ...(line.runMatrixMapping && { runMatrixMapping: line.runMatrixMapping }),
  };
};

/**
 * Reverse of `flattenCutFillsExtraItemsToBoqLines` — reconstructs
 * `cutFillsExtraItems`/`foundationsExtraItems` buckets from a fetched
 * `summary.cutFills`/`summary.foundations` array. `state.groundworks.cutFillsExtraItems`
 * is a GroundworksData field like `dynamicBillSummaryTabs`, not part of the
 * computed `summary` tree itself, so whatever reload path repopulates
 * `state.groundworks` from a fetched BOQ document has no other way to see it —
 * same reason `dynamicBillSummaryTabs` needs its own
 * `deserializeDynamicBillSummaryTabs` hydrate step. Extra items are identified
 * by `fieldName` starting with `"extra"` (the id shape `genExtraNodeId` —
 * useBillSummaryExtraItems.tsx — always produces; a real computed field's
 * `fieldName` is always a human-readable domain name, never that shape), and
 * re-grouped by their persisted `category` (the attachment key). A section
 * line's own `children` (see `extraNodeToBoqLine`) recursively rebuilds the
 * section wrapper and its nested content — a line with no `children` array is
 * always a leaf item, one with `children` (even `[]`) is always a section.
 */
export function deserializeCutFillsExtraItems(
  cutFillsSummary: unknown,
): Record<string, DynamicBillSummaryNode[]> | undefined {
  if (!Array.isArray(cutFillsSummary)) return undefined;
  const buckets: Record<string, DynamicBillSummaryNode[]> = {};
  cutFillsSummary.forEach((item: unknown) => {
    const line = item as Partial<BoqSummaryLineItem> | undefined;
    if (!line || typeof line.fieldName !== "string" || !line.fieldName.startsWith("extra")) return;
    const attachKey = typeof line.category === "string" && line.category ? line.category : "__root__";
    (buckets[attachKey] ??= []).push(boqLineToExtraNode(line));
  });
  return Object.keys(buckets).length > 0 ? buckets : undefined;
}

// Drainage Bill Summary's own extra-items pair — same tree/round-trip logic
// as extraNodeToBoqLine/boqLineToExtraNode above, but DrainageSummaryLineItem
// stores quantity/rate/amount as strings (matching every other drainage row
// already built that way in DrainageBillSummary.tsx) instead of
// BoqSummaryLineItem's numbers, so it can't reuse those two directly.
const extraNodeToDrainageLine = (node: DynamicBillSummaryNode, attachKey: string): DrainageSummaryLineItem => {
  if (isDynamicBillSummarySectionNode(node)) {
    return {
      key: node.id,
      fieldName: node.id,
      description: node.label,
      quantity: "",
      unit: "",
      rate: "",
      amount: computeDynamicBillSummaryNodeAmount(node).toFixed(2),
      category: attachKey,
      rowType: "main-header",
      children: node.children.map((child) => extraNodeToDrainageLine(child, node.id)),
    };
  }
  const amount = computeDynamicBillSummaryNodeAmount(node);
  return {
    key: node.id,
    fieldName: node.id,
    description: node.description,
    quantity: node.qty != null ? String(node.qty) : "",
    unit: node.unit ?? "",
    rate: node.rate != null ? node.rate.toFixed(2) : "",
    amount: node.amountType === "text" ? "" : amount ? amount.toFixed(2) : "",
    category: attachKey,
    rowType: "line-item",
    resourceCode: combineResourceCodes(node),
    amountType: node.amountType,
    amountText: node.amountText,
    plotMapping: node.plotMapping,
    runMatrixMapping: node.runMatrixMapping,
  };
};

/** Drainage counterpart of `flattenCutFillsExtraItemsToBoqLines` — see that
 *  function's own doc comment for the full rationale (persists sections as
 *  their own nested-`children` line instead of flattening them away). */
export function flattenDrainageExtraItemsToBoqLines(
  buckets: Record<string, DynamicBillSummaryNode[]> | undefined,
): DrainageSummaryLineItem[] {
  if (!buckets) return [];
  const out: DrainageSummaryLineItem[] = [];
  Object.entries(buckets).forEach(([attachKey, bucket]) => {
    bucket.forEach((node) => out.push(extraNodeToDrainageLine(node, attachKey)));
  });
  return out;
}

const drainageLineToExtraNode = (line: Partial<DrainageSummaryLineItem>): DynamicBillSummaryNode => {
  if (Array.isArray(line.children)) {
    return {
      id: line.key ?? line.fieldName ?? "",
      type: "section",
      label: line.description ?? "",
      children: line.children.map(drainageLineToExtraNode),
    };
  }
  const isText = line.amountType === "text";
  return {
    id: line.key ?? line.fieldName ?? "",
    type: "item",
    description: line.description ?? "",
    qty: isText ? undefined : (line.quantity !== undefined && line.quantity !== "" ? parseFloat(line.quantity) : undefined),
    unit: isText ? undefined : line.unit,
    rate: isText ? undefined : (line.rate !== undefined && line.rate !== "" ? parseFloat(line.rate) : undefined),
    amountType: isText ? "text" : "number",
    ...(isText && { amountText: line.amountText ?? "" }),
    ...((): { resourceCode?: string; resourceCodes?: DynamicBillSummaryResourceCodeEntry[] } => {
      const resourceCodes = splitResourceCode(line.resourceCode);
      return resourceCodes ? { resourceCodes } : line.resourceCode ? { resourceCode: line.resourceCode } : {};
    })(),
    ...(line.plotMapping && { plotMapping: line.plotMapping }),
    ...(line.runMatrixMapping && { runMatrixMapping: line.runMatrixMapping }),
  };
};

/** Drainage counterpart of `deserializeCutFillsExtraItems` — see that
 *  function's own doc comment for the full rationale. `fieldName` is
 *  optional on `DrainageSummaryLineItem` (unlike `BoqSummaryLineItem`), so
 *  extra rows are matched the same way but tolerate its absence. */
export function deserializeDrainageExtraItems(
  drainageSummary: unknown,
): Record<string, DynamicBillSummaryNode[]> | undefined {
  if (!Array.isArray(drainageSummary)) return undefined;
  const buckets: Record<string, DynamicBillSummaryNode[]> = {};
  drainageSummary.forEach((item: unknown) => {
    const line = item as Partial<DrainageSummaryLineItem> | undefined;
    if (!line || typeof line.fieldName !== "string" || !line.fieldName.startsWith("extra")) return;
    const attachKey = typeof line.category === "string" && line.category ? line.category : "__root__";
    (buckets[attachKey] ??= []).push(drainageLineToExtraNode(line));
  });
  return Object.keys(buckets).length > 0 ? buckets : undefined;
}
